import React, { useEffect, useMemo, useState } from 'react';
import AccountsComponent from './AccountsComponent.jsx';
import { ACCOUNT_TYPES, ENTITIES, getIdWithType } from 'users/containers/Organization/consts.js';
import { getIsAccountSelected } from '~/users/containers/Organization/components/LinkedAccounts/dataAccessHelperFunctions.js';
import { cloneDeep } from 'lodash';

const ENABLE_LOGS = false;

function debugLog(...args) {
  if (ENABLE_LOGS === true) {
    /* eslint-disable no-console */
    console.log('LinkedAccountsComponentWrapper.jsx:', ...args);
    /* eslint-enable no-console */
  }
}

function getFullAccessData(dataAccessWithFullAccess, additionalData) {
  let data = {};
  if (dataAccessWithFullAccess) {
    data.isFullAccess = true;
    data.isFullAccessInherited = dataAccessWithFullAccess?.derivedFromRole?.id !== additionalData.roleId;
  }
  return data;
}

const AccountsComponentWrapper = ({
  additionalData,
  allAccountsData,
  entity = ENTITIES.ROLE.id,
  isCreateEditMode,
  isViewOnly,
  linkedAccountsColumns,
  onSelectionChange,
  payerAccountsColumns,
  accountsModifiedSelectionByIdExternal = null,
  setAccountsModifiedSelectionByIdExternal = null,
}) => {
  const [fullAccessData, setFullAccessData] = useState(() => {
    return getFullAccessData(allAccountsData?.dataAccessWithFullAccess, additionalData);
  });

  useEffect(() => {
    if (!isCreateEditMode) {
      const fullAccessDataTemp = getFullAccessData(allAccountsData?.dataAccessWithFullAccess, additionalData);
      setFullAccessData(fullAccessDataTemp);
    }
  }, [additionalData, allAccountsData?.dataAccessWithFullAccess, isCreateEditMode]);

  const transformedAllAccountsData = useMemo(() => {
    if (allAccountsData) {
      // In AWS PayerAccount is also a linked account in this payer account, and it has the same id.
      // In order to distinguish between them - adding a prefix to the payer and linked accounts ids.
      let dataTransformed = cloneDeep(allAccountsData?.accountsData);
      dataTransformed?.forEach((pa) => {
        pa.payerAccount.originalId = pa.payerAccount.id;
        pa.payerAccount.id = getIdWithType({
          accountId: pa.payerAccount.id,
          accountType: ACCOUNT_TYPES.PAYER_ACCOUNT,
          cloudType: pa.payerAccount.cloudType,
        });
        pa.linkedAccounts.forEach((la) => {
          la.originalId = la.id;
          la.id = getIdWithType({
            accountId: la.id,
            accountType: ACCOUNT_TYPES.LINKED_ACCOUNT,
            payerAccountId: pa.payerAccount.id,
            cloudType: la.cloudType,
          });
        });
      });

      return { accountsData: dataTransformed, fullAccessData: fullAccessData };
    }
  }, [allAccountsData, fullAccessData]);

  const handleFullAccessSelectChange = (isSelected) => {
    if (isViewOnly) {
      return;
    }
    setFullAccessData({ isFullAccess: isSelected, isFullAccessInherited: false });
    if (isSelected) {
      setModifiedSelectionById({});
    }
  };

  const [modifiedSelectionById, setModifiedSelectionById] = useState(accountsModifiedSelectionByIdExternal || {});

  useEffect(() => {
    // On unmount
    return () => {
      if (setAccountsModifiedSelectionByIdExternal) {
        setAccountsModifiedSelectionByIdExternal(modifiedSelectionById);
      }
    };
  }, [modifiedSelectionById, setAccountsModifiedSelectionByIdExternal]);

  function updateModifiedSelection(newModifiedSelectionById, account, allSelectedIds, childAccounts) {
    const isSelectedNew = allSelectedIds?.includes(account.id);
    const isSelected = getIsAccountSelected(account, newModifiedSelectionById);
    if (!!isSelectedNew !== !!isSelected && account.isSelectionEnabled !== false) {
      debugLog('[updateModifiedSelection] isSelected do not match:', account, 'allSelectedIds:', allSelectedIds);
      if (modifiedSelectionById[account.id] === undefined) {
        newModifiedSelectionById[account.id] = isSelectedNew;
        debugLog('[updateModifiedSelection] newModifiedSelectionById value:', isSelectedNew);
      } else {
        delete newModifiedSelectionById[account.id];
        debugLog('[updateModifiedSelection] deleted newModifiedSelectionById value:', account.id);
      }

      // if payer account is modified by user, we reset all linked accounts modifications
      childAccounts?.forEach((childAccount) => {
        if (getIsAccountSelected(childAccount, newModifiedSelectionById)) {
          if (newModifiedSelectionById[childAccount.id] === true) {
            delete newModifiedSelectionById[childAccount.id];
          } else {
            newModifiedSelectionById[childAccount.id] = false;
          }
        }
      });
    }
  }

  const selectionPayload = useMemo(() => {
    if (!transformedAllAccountsData?.accountsData) {
      return null;
    }

    if (fullAccessData?.isFullAccess === true) {
      return {
        allLinkedAccountsOfAllPayerAccounts: true,
      };
    }

    const payload = {
      accountsToAdd: {
        fullyAssignedPayerAccounts: [],
        payerWithLinkedAccounts: {},
      },
      accountsToRemove: {
        fullyAssignedPayerAccounts: [],
        payerWithLinkedAccounts: {},
      },
      allLinkedAccountsOfAllPayerAccounts: false,
    };

    transformedAllAccountsData?.accountsData.forEach((account) => {
      const { payerAccount, linkedAccounts } = account;
      const modifiedPayerSelection = modifiedSelectionById[payerAccount.id];
      if (modifiedPayerSelection !== undefined) {
        if (modifiedPayerSelection === true && isCreateEditMode === true) {
          payload.accountsToAdd.fullyAssignedPayerAccounts.push(payerAccount.originalId);
        } else {
          payload.accountsToRemove.fullyAssignedPayerAccounts.push(payerAccount.originalId);
        }
      }
      linkedAccounts.forEach((linkedAccount) => {
        const modifiedLinkedSelection = modifiedSelectionById[linkedAccount.id];
        if (modifiedLinkedSelection !== undefined) {
          if (modifiedLinkedSelection === true && isCreateEditMode === true) {
            if (payload.accountsToAdd.payerWithLinkedAccounts[payerAccount.originalId] === undefined) {
              payload.accountsToAdd.payerWithLinkedAccounts[payerAccount.originalId] = [];
            }
            payload.accountsToAdd.payerWithLinkedAccounts[payerAccount.originalId].push(linkedAccount.originalId);
          } else {
            if (payload.accountsToRemove.payerWithLinkedAccounts[payerAccount.originalId] === undefined) {
              payload.accountsToRemove.payerWithLinkedAccounts[payerAccount.originalId] = [];
            }
            payload.accountsToRemove.payerWithLinkedAccounts[payerAccount.originalId].push(linkedAccount.originalId);
          }
        }
      });
    });
    return payload;
  }, [transformedAllAccountsData?.accountsData, fullAccessData?.isFullAccess, modifiedSelectionById, isCreateEditMode]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectionPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionPayload]);

  const handleSelectionChange = ({ type, filteredInAccountIds, allSelectedIds, parentPayerAccount }) => {
    if (isViewOnly) {
      return;
    }
    debugLog(
      '[handleSelectionChange] Invoked with type:',
      type,
      'allSelectedIds:',
      allSelectedIds,
      'parentPayerAccount:',
      parentPayerAccount,
    );

    const newModifiedSelectionById = { ...modifiedSelectionById };
    transformedAllAccountsData?.accountsData.forEach((account) => {
      const { payerAccount, linkedAccounts } = account;

      if (type === ACCOUNT_TYPES.PAYER_ACCOUNT && filteredInAccountIds.includes(payerAccount.id)) {
        updateModifiedSelection(newModifiedSelectionById, payerAccount, allSelectedIds, linkedAccounts);
      } else if (type === ACCOUNT_TYPES.LINKED_ACCOUNT && parentPayerAccount?.id === payerAccount.id) {
        linkedAccounts?.forEach((linkedAccount) => {
          if (filteredInAccountIds.includes(linkedAccount.id)) {
            updateModifiedSelection(newModifiedSelectionById, linkedAccount, allSelectedIds);
          }
        });
      }
    });

    setModifiedSelectionById(newModifiedSelectionById);

    debugLog('[handleSelectionChange] modifiedSelectionById:', modifiedSelectionById);
  };

  const handleSelectAllChange = (selectAllInfo) => {
    if (isViewOnly)  {
      return;
    }
    debugLog('[handleSelectAllChange]', selectAllInfo);

    const newModifiedSelectionById = {};
    transformedAllAccountsData?.accountsData.forEach((account) => {
      const { payerAccount, linkedAccounts } = account;
      if (payerAccount) {
        updateModifiedSelection(
          newModifiedSelectionById,
          payerAccount,
          selectAllInfo[ACCOUNT_TYPES.PAYER_ACCOUNT],
          linkedAccounts,
        );
      }

      if (linkedAccounts) {
        linkedAccounts.forEach((linkedAccount) => {
          const selectedLinkedAccountsIds =
            selectAllInfo[ACCOUNT_TYPES.LINKED_ACCOUNT]
              .find((selectedLinkedAccounts) => selectedLinkedAccounts.payerAccount.id === payerAccount.id)
              ?.linkedAccounts.map((la) => la.id) ?? [];

          updateModifiedSelection(newModifiedSelectionById, linkedAccount, selectedLinkedAccountsIds);
        });
      }
    });
    setModifiedSelectionById(newModifiedSelectionById);

    debugLog('[handleSelectionChange] modifiedSelectionById:', modifiedSelectionById);
  };

  return transformedAllAccountsData ? (
    <AccountsComponent
      entity={entity}
      additionalData={additionalData}
      allAccountsData={transformedAllAccountsData}
      isCreateEditMode={isCreateEditMode}
      isViewOnly={isViewOnly}
      linkedAccountsColumns={linkedAccountsColumns}
      modifiedSelectionById={modifiedSelectionById}
      onSelectionChange={handleSelectionChange}
      onSelectAllChanged={handleSelectAllChange}
      onFullAccessSelectChanged={handleFullAccessSelectChange}
      payerAccountsColumns={payerAccountsColumns}
    />
  ) : (
    <div className="text-center">
      <p>No accounts data available.</p>
    </div>
  );
};


export default AccountsComponentWrapper;
